import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    AppBar,
    Toolbar,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    Typography,
    Box,
    Chip,
    Divider,
    useTheme,
    useMediaQuery,
    Autocomplete,
    Paper,
    Fade,
    Tooltip,
    Badge,
} from '@mui/material';
import ThemeToggle from '../shared/ThemeToggle';
import NavRoleHint from './NavRoleHint';
import { NAV_GLOSSARY } from '../../constants/navGlossary';
import { filterDeliveryCockpitNavItems, filterDeliveryCockpitSearchData } from '../../constants/deliveryCockpitNav';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Assignment as ProjectsIcon,
    Task as TasksIcon,
    Schedule as TimesheetsIcon,
    AttachMoney as CostIcon,
    Assessment as ReportsIcon,
    Analytics as AnalyticsIcon,
    Settings as AdminIcon,
    People as UsersIcon,
    Search as SearchIcon,
    ExitToApp as LogoutIcon,
    Person as ProfileIcon,
    Notifications as NotificationsIcon,
    Psychology as PsychologyIcon,
    SmartToy as IntelligenceIcon,
    AccountBalance as QuickBooksIcon,
    AccountBalanceWallet as XeroIcon,
    Receipt as InvoiceIcon,
    AccountBalance as RevenueIcon,
    Link as IntegrationIcon,
    Palette as PaletteIcon,
    ReceiptLong as ExpenseIcon,
    Support as HelpDeskIcon,
    HelpOutline as HelpTourIcon,
    MenuBook as GuideIcon,
    LocationOn as GPSIcon,
    Business as CRMIcon,
    Message as MessageIcon,
    Insights as ObservabilityIcon,
    Folder as DocumentControlIcon,
    Timeline as GanttChartIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePendingTimesheetApprovalCount } from '../../hooks/usePendingTimesheetApprovalCount';
import { useDeliveryCockpitHelp } from '../../contexts/DeliveryCockpitHelpContext';
import { styled } from '@mui/material/styles';

// Styled components for enhanced visual hierarchy
const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    zIndex: theme.zIndex.drawer + 1,
}));

// 221px paper width so client width meets design-review #10 (≥220px) with 1px divider border.
const DRAWER_WIDTH = 221;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: DRAWER_WIDTH,
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        paddingTop: theme.spacing(1),
    },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active }) => ({
    margin: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    minHeight: 44, // Touch target size
    transition: theme.transitions.create(['background-color', 'box-shadow'], {
        duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main + '15',
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.main + '25',
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: '60%',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '0 2px 2px 0',
        },
    },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
    width: '100%',
    minWidth: 220,
    maxWidth: 400,
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(2),
        width: 'auto',
        flex: '1 1 220px',
    },
}));

const UserSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 'auto',
}));

/** Max wedge-relevant sidebar items for PM/manager daily nav (V7-GATE-2). */
export const WEDGE_PM_SIDEBAR_MAX_ITEMS = 6;

// Navigation items configuration
export const getNavigationItems = (userRole) => {
    // Base items for all users
    const commonItems = [
        { id: 'timesheets', label: 'Timesheets', icon: TimesheetsIcon, path: '/timesheets' },
        { id: 'expenses', label: 'Expense Management', icon: ExpenseIcon, path: '/expenses' },
        { id: 'reports', label: 'Reports', icon: ReportsIcon, path: '/reports' },
        { id: 'helpdesk', label: 'Help Desk', icon: HelpDeskIcon, path: '/helpdesk' },
        { id: 'gps', label: 'GPS Field Tracking', icon: GPSIcon, path: '/gps-field-tracking' },
    ];

    // Role-based navigation items following Phase 3.1 consolidation
    const getBaseItemsForRole = (role) => {
        // C-Level Executives - Strategic Overview (including admin)
        if (['director', 'ceo', 'cto', 'cfo', 'admin'].includes(role)) {
            return [
                { id: 'project-operations', label: 'Projects', icon: ProjectsIcon, path: '/project-operations' },
                ...commonItems,
            ];
        }

        // Managers - Operational Management
        if (['project_manager', 'manager', 'admin'].includes(role)) {
            return [
                { id: 'project-operations', label: 'Projects', icon: ProjectsIcon, path: '/project-operations' },
                ...commonItems,
            ];
        }

        // Finance wedge — billing and expense oversight (not IC my-workspace)
        if (['finance', 'accountant'].includes(role)) {
            return [
                { id: 'invoice-management', label: 'Billing & Invoices', icon: InvoiceIcon, path: '/invoice-management' },
                { id: 'expenses', label: 'Expense Management', icon: ExpenseIcon, path: '/expenses' },
                { id: 'timesheets', label: 'Timesheets', icon: TimesheetsIcon, path: '/timesheets' },
            ];
        }

        // Individual Contributors - Daily Workspace
        return [
            { id: 'my-workspace', label: 'My Workspace', icon: TasksIcon, path: '/my-workspace' },
            ...commonItems
        ];
    };

    const baseItems = getBaseItemsForRole(userRole);

    // Financial Management - For Finance Teams and Directors
    const financialItems = [];
    if (['finance', 'manager', 'director', 'cfo', 'admin'].includes(userRole)) {
        financialItems.push(
            { id: 'financial-management', label: 'Financial Management', icon: CostIcon, path: '/financial-management' }
        );
    }

    // Business Intelligence & Analytics - For Analysts and Data-driven roles
    const analyticsItems = [];
    if (['analyst', 'business_analyst', 'director', 'manager', 'admin'].includes(userRole)) {
        analyticsItems.push(
            { id: 'analytics-dashboard', label: 'Analytics & BI Center', icon: AnalyticsIcon, path: '/analytics-dashboard' },
            { id: 'ai-intelligence', label: 'AI Intelligence Chat', icon: IntelligenceIcon, path: '/ai-intelligence' }
        );
    }

    // Observability & Monitoring - Operational observability tools
    const observabilityItems = [];
    if (['admin', 'director', 'manager', 'cfo', 'cto', 'ceo'].includes(userRole)) {
        observabilityItems.push(
            { id: 'observability', label: 'Observability', icon: ObservabilityIcon, path: '/observability' }
        );
    }

    // Resource Planning - Strategic for Executives, Operational for Managers
    const resourceItems = [];
    if (['director', 'ceo', 'manager', 'project_manager', 'admin'].includes(userRole)) {
        resourceItems.push(
            { id: 'resource-planning', label: 'Resource Planning', icon: PsychologyIcon, path: '/resource-planning' },
            // ML features integrated into Resource Planning Dashboard (industry standard)
        );
    }

    // PM Integration - For Project Managers and Administrators
    const integrationItems = [];
    if (['project_manager', 'manager', 'admin'].includes(userRole)) {
        integrationItems.push(
            { id: 'pm-integration', label: 'PM Integration', icon: IntegrationIcon, path: '/pm-integration' },
            { id: 'crm-integration', label: 'CRM Integration', icon: CRMIcon, path: '/crm-integration' },
            { id: 'communication', label: 'Communication Platforms', icon: MessageIcon, path: '/communication' }
        );
    }

    // Document Control - For Project Managers and Administrators
    const documentControlItems = [];
    if (['project_manager', 'manager', 'admin'].includes(userRole)) {
        documentControlItems.push(
            { id: 'document-control', label: 'Document Control', icon: DocumentControlIcon, path: '/document-control' }
        );
    }

    // Gantt Chart - For Project Managers and Administrators
    const ganttChartItems = [];
    if (['project_manager', 'manager', 'admin'].includes(userRole)) {
        ganttChartItems.push(
            { id: 'gantt-chart', label: 'Project Scheduling', icon: GanttChartIcon, path: '/gantt-chart' }
        );
    }

    // Accounting & Billing — wedge invoice path for PM/manager/finance; full GL sync deferred.
    const accountingItems = [];
    if (['finance', 'admin', 'manager', 'project_manager'].includes(userRole)) {
        accountingItems.push(
            { id: 'invoice-management', label: 'Billing & Invoices', icon: InvoiceIcon, path: '/invoice-management' },
        );
    }
    if (['finance', 'admin', 'manager'].includes(userRole)) {
        accountingItems.push(
            { id: 'revenue-recognition', label: 'Revenue Recognition', icon: RevenueIcon, path: '/revenue-recognition' },
            { id: 'quickbooks', label: 'QuickBooks', icon: QuickBooksIcon, path: '/quickbooks' },
            { id: 'xero', label: 'Xero', icon: XeroIcon, path: '/xero' }
        );
    }

    // Administrative Tools - Admin only
    const adminItems = [];
    if (userRole === 'admin') {
        adminItems.push(
            { id: 'admin', label: 'Admin', icon: AdminIcon, path: '/admin' },
            { id: 'users', label: 'Users', icon: UsersIcon, path: '/users' },
            { id: 'tenant-management', label: 'Tenant Management', icon: AdminIcon, path: '/admin/tenants' },
            { id: 'theme-showcase', label: 'Theme Showcase', icon: PaletteIcon, path: '/theme-showcase' }
        );
    }

    // Build complete navigation structure following business workflow logic
    const operationsSection = [...baseItems];

    // 3. Planning & Strategy - Resource and project planning
    const planningSection = [...resourceItems, ...integrationItems, ...documentControlItems, ...ganttChartItems];

    // 4. Financial Management - Budget and cost management
    const financialSection = [...financialItems];

    // 5. Analytics & Reporting - Business intelligence and insights
    const analyticsSection = [...analyticsItems, ...observabilityItems];

    // 6. Administrative - Accounting and system administration
    const adminSection = [...accountingItems, ...adminItems];

    return filterDeliveryCockpitNavItems([
        ...operationsSection,
        ...planningSection,
        ...financialSection,
        ...analyticsSection,
        ...adminSection,
    ]);
};

// Search data for intelligent filtering - dynamically filtered by user role
const getSearchData = (userRole) => {
    // Base search data for all users
    const commonData = [
        { type: 'page', label: 'Overview Dashboard', path: '/overview-dashboard', keywords: ['overview', 'dashboard', 'summary', 'home', 'main', 'start', 'welcome', 'kpi', 'metrics'] },
        { type: 'page', label: 'Timesheets', path: '/timesheets', keywords: ['time', 'hours', 'tracking', 'log', 'entry'] },
        { type: 'page', label: 'Expense Management', path: '/expenses', keywords: ['expenses', 'receipts', 'reimbursement', 'cost', 'money', 'travel', 'ocr', 'receipt', 'expense report'] },
        { type: 'page', label: 'Help Center', path: '/help', keywords: ['help', 'guide', 'documentation', 'how to', 'tutorial', 'screenshots', 'manual', 'support'] },
        { type: 'page', label: 'Help Desk', path: '/helpdesk', keywords: ['support', 'tickets', 'issues', 'problems', 'troubleshooting', 'knowledge base', 'faq'] },
        { type: 'page', label: 'GPS Field Tracking', path: '/gps-field-tracking', keywords: ['gps', 'location', 'field work', 'tracking', 'attendance', 'geofencing', 'mobile'] },
        { type: 'page', label: 'Reports', path: '/reports', keywords: ['analytics', 'data', 'statistics', 'export'] },
        { type: 'action', label: 'Submit Timesheet', path: '/timesheets/submit', keywords: ['submit', 'send', 'approval'] },
        { type: 'action', label: 'Create Expense Report', path: '/expenses', keywords: ['create', 'add', 'new', 'expense', 'report'] },
        { type: 'action', label: 'Submit Help Ticket', path: '/helpdesk', keywords: ['create', 'submit', 'ticket', 'issue', 'problem'] }
    ];

    // Role-based search data following Phase 3.1 consolidation
    const getSearchDataForRole = (role) => {
        // C-Level Executives - Strategic search (including admin)
        if (['director', 'ceo', 'cto', 'cfo', 'admin'].includes(role)) {
            return [
                { type: 'page', label: 'Executive Dashboard', path: '/executive-dashboard', keywords: ['strategic', 'portfolio', 'kpi', 'executive', 'overview', 'performance', 'health', 'investment', 'roi'] },
                { type: 'page', label: 'Project Operations', path: '/project-operations', keywords: ['projects', 'tasks', 'operations', 'management', 'oversight', 'assignments', 'team'] },
                ...commonData
            ];
        }

        // Managers - Operational search
        if (['project_manager', 'manager', 'admin'].includes(role)) {
            return [
                { type: 'page', label: 'Project Operations', path: '/project-operations', keywords: ['projects', 'tasks', 'operations', 'management', 'oversight', 'assignments', 'team'] },
                ...commonData,
                { type: 'action', label: 'Create New Project', path: '/project-operations', keywords: ['add', 'new', 'create', 'project'] },
                { type: 'action', label: 'Assign Task', path: '/project-operations', keywords: ['assign', 'task', 'delegate', 'team'] }
            ];
        }

        // Individual Contributors - Personal workspace search
        return [
            { type: 'page', label: 'My Workspace', path: '/my-workspace', keywords: ['workspace', 'tasks', 'work', 'assignments', 'todo', 'personal', 'daily'] },
            ...commonData,
            { type: 'action', label: 'Log Time Entry', path: '/my-workspace', keywords: ['log', 'time', 'hours', 'work', 'entry'] },
            { type: 'action', label: 'Update Task Status', path: '/my-workspace', keywords: ['update', 'complete', 'status', 'progress'] }
        ];
    };

    const baseSearchData = getSearchDataForRole(userRole);

    // Financial Management - Finance teams and Directors
    if (['finance', 'manager', 'director', 'cfo', 'admin'].includes(userRole)) {
        baseSearchData.push({
            type: 'page',
            label: 'Financial Management',
            path: '/financial-management',
            keywords: ['financial', 'costs', 'budget', 'finance', 'money', 'profit', 'expenses', 'revenue', 'forecasting', 'margins', 'profitability', 'variance']
        });
    }

    // Analytics & BI Center - Analysts and Data-driven roles
    if (['analyst', 'business_analyst', 'director', 'manager', 'admin'].includes(userRole)) {
        baseSearchData.push({
            type: 'page',
            label: 'Analytics & BI Center',
            path: '/analytics-dashboard',
            keywords: ['analytics', 'business intelligence', 'bi', 'statistical', 'forecasting', 'trends', 'insights', 'reports', 'data analysis', 'predictive', 'custom reports', 'alerts']
        });
        baseSearchData.push({
            type: 'page',
            label: 'Advanced Analytics & AI',
            path: '/analytics-dashboard?view=predictive',
            keywords: ['advanced analytics', 'ai', 'artificial intelligence', 'machine learning', 'predictive analytics', 'forecasting', 'anomaly detection', 'risk assessment', 'ai insights', 'intelligent insights']
        });
        baseSearchData.push({
            type: 'page',
            label: 'AI Intelligence Chat',
            path: '/ai-intelligence',
            keywords: ['ai chat', 'intelligent assistant', 'nlp', 'natural language', 'ask questions', 'ai assistant', 'intelligence', 'query', 'chat', 'conversation', 'questions', 'answers']
        });
    }
    if (['admin', 'director', 'manager', 'cfo', 'cto', 'ceo'].includes(userRole)) {
        baseSearchData.push({
            type: 'page',
            label: 'Observability',
            path: '/observability',
            keywords: ['observability', 'monitoring', 'slo', 'kpi', 'alerts', 'health check', 'uptime', 'metrics']
        });
    }

    // Resource Planning - Strategic and Operational planning
    if (['director', 'ceo', 'manager', 'project_manager', 'admin'].includes(userRole)) {
        baseSearchData.push({
            type: 'page',
            label: 'Resource Planning',
            path: '/resource-planning',
            keywords: ['resources', 'planning', 'capacity', 'allocation', 'team', 'utilization', 'skills', 'staffing', 'workforce', 'rates', 'billing', 'pricing', 'cost', 'hourly', 'billable', 'escalation', 'rate cards', 'management', 'pricing structure']
        });
    }

    // PM Integration - Project Managers and Administrators
    if (['project_manager', 'manager', 'admin'].includes(userRole)) {
        baseSearchData.push({
            type: 'page',
            label: 'PM Integration',
            path: '/pm-integration',
            keywords: ['integration', 'ms project', 'primavera', 'smartsheet', 'project management', 'import', 'export', 'sync', 'enterprise', 'pm tools', 'scheduling', 'gantt']
        });
        baseSearchData.push({
            type: 'page',
            label: 'CRM Integration',
            path: '/crm-integration',
            keywords: ['crm', 'salesforce', 'hubspot', 'leads', 'opportunities', 'contacts', 'accounts', 'sales', 'customer relationship', 'pipeline', 'prospects', 'deals', 'bidirectional sync']
        });
        baseSearchData.push({
            type: 'page',
            label: 'Communication Platforms',
            path: '/communication',
            keywords: ['communication', 'slack', 'teams', 'microsoft teams', 'notifications', 'channels', 'collaboration', 'chat', 'messaging', 'webhooks', 'integration', 'automated notifications', 'alerts']
        });
        baseSearchData.push({
            type: 'page',
            label: 'Document Control',
            path: '/document-control',
            keywords: ['document', 'documents', 'file management', 'document control', 'version control', 'approvals', 'document approval', 'file upload', 'document management', 'documents', 'files', 'attachments', 'document workflow']
        });
        baseSearchData.push({
            type: 'page',
            label: 'Project Scheduling',
            path: '/gantt-chart',
            keywords: ['gantt', 'gantt chart', 'timeline', 'scheduling', 'project timeline', 'schedule', 'project management', 'task timeline', 'milestones', 'critical path', 'dependencies', 'resource leveling', 'project planning']
        });
    }

    // Accounting & Billing - Finance and Admin
    if (['finance', 'admin', 'manager'].includes(userRole)) {
        baseSearchData.push(
            {
                type: 'page',
                label: 'Revenue Recognition',
                path: '/revenue-recognition',
                keywords: ['revenue', 'recognition', 'asc 606', 'gaap', 'compliance', 'accounting', 'financial', 'contracts', 'performance obligations']
            },
            {
                type: 'page',
                label: 'Invoice Management',
                path: '/invoice-management',
                keywords: ['invoices', 'billing', 'management', 'templates', 'automation', 'accounts receivable', 'payment', 'overdue']
            },
            {
                type: 'page',
                label: 'QuickBooks',
                path: '/quickbooks',
                keywords: ['accounting', 'quickbooks', 'invoices', 'billing', 'customers', 'sync', 'integration', 'financial']
            },
            {
                type: 'page',
                label: 'Xero',
                path: '/xero',
                keywords: ['accounting', 'xero', 'invoices', 'billing', 'customers', 'sync', 'integration', 'financial']
            }
        );
    }

    // Administrative Tools - Admin only
    if (userRole === 'admin') {
        baseSearchData.push(
            { type: 'page', label: 'Admin', path: '/admin', keywords: ['admin', 'administration', 'system', 'settings', 'configuration'] },
            { type: 'page', label: 'Users', path: '/users', keywords: ['users', 'people', 'accounts', 'permissions', 'roles'] },
            { type: 'page', label: 'Tenant Management', path: '/admin/tenants', keywords: ['tenants', 'organizations', 'clients', 'multi-tenant'] }
        );
    }

    return filterDeliveryCockpitSearchData(baseSearchData, userRole);
};

const EnhancedNavigation = ({
    mobileMenuOpen: controlledMobileOpen,
    setMobileMenuOpen,
} = {}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    // State management — App.js controls drawer when setMobileMenuOpen is provided.
    const [uncontrolledMobileOpen, setUncontrolledMobileOpen] = useState(false);
    const isDrawerControlled = typeof setMobileMenuOpen === 'function';
    const mobileOpen = isDrawerControlled ? Boolean(controlledMobileOpen) : uncontrolledMobileOpen;
    const setMobileOpen = isDrawerControlled ? setMobileMenuOpen : setUncontrolledMobileOpen;
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [helpMenuAnchor, setHelpMenuAnchor] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchOpen, setSearchOpen] = useState(false);
    // Server-sourced unread count when notification feed API is wired; 0 = empty state (#78).
    const notificationCount = 0;
    const {
        count: pendingTimesheetApprovalCount,
        canApprove: canSeePendingTimesheetBadge,
    } = usePendingTimesheetApprovalCount();
    const {
        openFullTour,
        resumeTour,
        restartTour,
        hasSavedProgress,
    } = useDeliveryCockpitHelp();

    // Navigation items based on user role
    const navigationItems = useMemo(() => getNavigationItems(user?.role), [user?.role]);

    // Close help menu on route change so it cannot overlay timesheet Open / dialogs.
    useEffect(() => {
        setHelpMenuAnchor(null);
    }, [location.pathname]);

    // Handle mobile drawer toggle
    const handleDrawerToggle = useCallback(() => {
        setMobileOpen(!mobileOpen);
    }, [mobileOpen]);

    // Handle navigation
    const handleNavigation = useCallback((path, dynamicPath = false) => {
        // For document control, always navigate to base path (project selector will handle project selection)
        setHelpMenuAnchor(null);
        navigate(path);
        if (isMobile) {
            setMobileOpen(false);
        }
    }, [navigate, isMobile]);

    // Handle user menu
    const handleUserMenuOpen = useCallback((event) => {
        setUserMenuAnchor(event.currentTarget);
    }, []);

    const handleUserMenuClose = useCallback(() => {
        setUserMenuAnchor(null);
    }, []);

    // Handle logout
    const handleLogout = useCallback(() => {
        logout();
        handleUserMenuClose();
        navigate('/login');
    }, [logout, navigate, handleUserMenuClose]);

    // Search functionality
    const handleSearchChange = useCallback((event, newValue) => {
        setSearchValue(newValue);

        if (newValue.length > 0) {
            const filtered = getSearchData(user?.role).filter(item =>
                item.label.toLowerCase().includes(newValue.toLowerCase()) ||
                item.keywords.some(keyword => keyword.toLowerCase().includes(newValue.toLowerCase()))
            );
            setSearchResults(filtered);
            setSearchOpen(true);
        } else {
            setSearchResults([]);
            setSearchOpen(false);
        }
    }, [user?.role]);

    const handleSearchSelect = useCallback((event, value) => {
        if (value) {
            navigate(value.path);
            setSearchValue('');
            setSearchOpen(false);
        }
    }, [navigate]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ctrl+K or Cmd+K to focus search
            if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
                event.preventDefault();
                document.getElementById('global-search')?.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Drawer content
    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Logo/Brand */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    component="img"
                    src="/synaptix-pulse-logo.svg"
                    alt="Synaptix Pulse"
                    sx={{
                        width: 64,
                        height: 64,
                        filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.15))',
                        border: '2px solid rgba(245, 166, 35, 0.1)',
                        borderRadius: '8px',
                        padding: '4px'
                    }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            lineHeight: 1.3,
                        }}
                    >
                        synaptix PULSE
                    </Typography>
                </Box>
            </Box>

            <Divider />

            <NavRoleHint role={user?.role} />

            {/* Navigation Items */}
            <List sx={{ flex: 1, py: 1 }}>
                {navigationItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path ||
                        (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                    const glossary = NAV_GLOSSARY[item.id];

                    return (
                        <Tooltip
                            key={item.id}
                            title={glossary || item.label}
                            placement="right"
                            enterDelay={400}
                        >
                            <StyledListItemButton
                                selected={isActive}
                                onClick={() => handleNavigation(item.path)}
                                data-tour={item.id === 'timesheets' ? 'tour-nav-timesheets' : undefined}
                                aria-label={
                                    item.id === 'timesheets' && pendingTimesheetApprovalCount > 0
                                        ? `Navigate to ${item.label} (${pendingTimesheetApprovalCount} pending approval)`
                                        : `Navigate to ${item.label}`
                                }
                                aria-current={isActive ? 'page' : undefined}
                            >
                                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                                    {item.id === 'timesheets' && canSeePendingTimesheetBadge && pendingTimesheetApprovalCount > 0 ? (
                                        <Badge
                                            badgeContent={pendingTimesheetApprovalCount}
                                            color="warning"
                                            data-testid="nav-timesheets-pending-badge"
                                        >
                                            <Icon />
                                        </Badge>
                                    ) : (
                                        <Icon />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }}
                                />
                            </StyledListItemButton>
                        </Tooltip>
                    );
                })}
            </List>

            {/* User Section */}
            <UserSection>
                <Avatar
                    src={user?.avatar}
                    alt="User Avatar"
                    sx={{ width: 40, height: 40, mr: 1.5, cursor: 'pointer' }}
                    onClick={handleUserMenuOpen}
                >
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                        {user?.first_name} {user?.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {user?.role}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={handleUserMenuOpen}
                    aria-label="User menu"
                    sx={{ ml: 1 }}
                >
                    <AdminIcon fontSize="small" />
                </IconButton>
            </UserSection>
        </Box>
    );

    return (
        <>
            {/* App Bar */}
            <StyledAppBar position="fixed" elevation={0}>
                <Toolbar>
                    {/* Mobile menu button */}
                    {isMobile && (
                        <IconButton
                            color="inherit"
                            aria-label="Open navigation menu"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    {/* Logo/Brand */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: { xs: 1, md: 3 },
                        minWidth: 0
                    }}>
                        <Box
                            component="img"
                            src="/synaptix-pulse-logo.svg"
                            alt="Synaptix Pulse"
                            sx={{
                                width: { xs: 72, md: 80 },
                                height: { xs: 72, md: 80 },
                                filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.2))',
                                flexShrink: 0,
                                border: '2px solid rgba(245, 166, 35, 0.1)',
                                borderRadius: '8px',
                                padding: '4px'
                            }}
                        />
                        <Box sx={{
                            minWidth: 0,
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                        }}>
                            <Typography
                                variant="subtitle1"
                                component="div"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    lineHeight: 1.3,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                synaptix PULSE
                            </Typography>
                        </Box>
                    </Box>

                    {/* Search */}
                    <SearchContainer>
                        <Autocomplete
                            id="global-search"
                            freeSolo
                            inputValue={searchValue}
                            open={searchOpen}
                            onOpen={() => setSearchOpen(true)}
                            onClose={() => setSearchOpen(false)}
                            options={searchResults}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={option.type}
                                        size="small"
                                        variant="outlined"
                                        sx={{ minWidth: 60, fontSize: '0.75rem' }}
                                    />
                                    <Typography variant="body2">{option.label}</Typography>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search projects, timesheets… (Ctrl+K)"
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                            minHeight: 44,
                                        },
                                    }}
                                />
                            )}
                            onInputChange={handleSearchChange}
                            onChange={handleSearchSelect}
                            PaperComponent={({ children, ...props }) => (
                                <Fade in={searchOpen}>
                                    <Paper {...props} elevation={8}>
                                        {children}
                                    </Paper>
                                </Fade>
                            )}
                        />
                    </SearchContainer>

                    <Box sx={{ flexGrow: 1 }} />

                    <Tooltip title="Help — tour or full guide">
                        <IconButton
                            color="inherit"
                            aria-label="Open delivery cockpit help guide"
                            aria-controls={helpMenuAnchor ? 'delivery-cockpit-help-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={helpMenuAnchor ? 'true' : undefined}
                            onClick={(event) => setHelpMenuAnchor(event.currentTarget)}
                            sx={{ mr: 1 }}
                            data-testid="nav-help-tour-button"
                        >
                            <HelpTourIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        id="delivery-cockpit-help-menu"
                        anchorEl={helpMenuAnchor}
                        open={Boolean(helpMenuAnchor)}
                        onClose={() => setHelpMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <MenuItem
                            onClick={() => {
                                setHelpMenuAnchor(null);
                                openFullTour();
                            }}
                            data-testid="nav-help-full-tour"
                        >
                            Full tour
                        </MenuItem>
                        {hasSavedProgress ? (
                            <MenuItem
                                onClick={() => {
                                    setHelpMenuAnchor(null);
                                    resumeTour();
                                }}
                                data-testid="nav-help-resume-tour"
                            >
                                Resume tour
                            </MenuItem>
                        ) : null}
                        <MenuItem
                            onClick={() => {
                                setHelpMenuAnchor(null);
                                restartTour();
                            }}
                            data-testid="nav-help-restart-tour"
                        >
                            Restart tour
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                setHelpMenuAnchor(null);
                                navigate('/help');
                            }}
                            data-testid="nav-help-center-link"
                        >
                            <ListItemIcon>
                                <GuideIcon fontSize="small" />
                            </ListItemIcon>
                            Full help guide
                        </MenuItem>
                    </Menu>

                    {/* Notifications (#78: badge when count > 0, empty-state label otherwise) */}
                    <Tooltip
                        title={notificationCount > 0
                            ? `${notificationCount} unread notification${notificationCount === 1 ? '' : 's'}`
                            : 'No notifications'}
                    >
                        <IconButton
                            color="inherit"
                            aria-label={notificationCount > 0
                                ? `${notificationCount} notifications`
                                : 'No notifications'}
                            sx={{ mr: 1 }}
                            data-testid="nav-notifications-button"
                        >
                            <Badge
                                badgeContent={notificationCount > 0 ? notificationCount : null}
                                color="error"
                                data-testid="nav-notification-badge"
                            >
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* Theme Toggle */}
                    <ThemeToggle variant="icon" />

                    {/* Desktop user avatar */}
                    {!isMobile && (
                        <IconButton
                            onClick={handleUserMenuOpen}
                            aria-label="User menu"
                            sx={{ p: 0 }}
                        >
                            <Avatar
                                src={user?.avatar}
                                alt="User Avatar"
                                sx={{ width: 32, height: 32 }}
                            >
                                {user?.first_name?.[0]}{user?.last_name?.[0]}
                            </Avatar>
                        </IconButton>
                    )}
                </Toolbar>
            </StyledAppBar>

            {/* Navigation Drawer */}
            <Box
                component="nav"
                sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
                aria-label="Main navigation"
                data-tour="tour-main-nav"
            >
                {isMobile ? (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
                        }}
                    >
                        {drawerContent}
                    </Drawer>
                ) : (
                    <StyledDrawer variant="permanent" open>
                        {drawerContent}
                    </StyledDrawer>
                )}
            </Box>

            {/* User Menu */}
            <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                onClick={handleUserMenuClose}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        mt: 1.5,
                        minWidth: 200,
                        '& .MuiMenuItem-root': {
                            minHeight: 44,
                            px: 2,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => navigate('/profile')}>
                    <ListItemIcon>
                        <ProfileIcon fontSize="small" />
                    </ListItemIcon>
                    Profile
                </MenuItem>
                <MenuItem onClick={() => navigate('/settings')}>
                    <ListItemIcon>
                        <AdminIcon fontSize="small" />
                    </ListItemIcon>
                    Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>
        </>
    );
};

export default EnhancedNavigation; 